import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { maxLength, z } from "zod";
import { createIfsc, updateIfsc } from "../../../services/apiService";
import { showErrorToast, showSuccessToast } from "../../../utils/toastUtils";
import { Loader, Loader2 } from "lucide-react";

export default function BankForm({ formType, dataToUpdate, setFormType }) {
  const schema = z.object({
    ifscCode: z.string().length(11, "Invalid IFSC"),
    bankName: z.string().min(2, "Bank name required"),
    branchName: z.string().min(2, "Branch name required"),
    district: z.string().min(2, "district required"),
    state: z.string().min(2, "State required"),
    center: z.string().optional(),
    cityName: z.string().optional(),
    micrCode: z.string().optional(),
    swiftCode: z.string().optional(),
    branchContact: z.string().optional(),
    branchAddress: z.string().optional(),
    imps: z.string().optional(),
    neft: z.string().optional(),
    rtgs: z.string().optional(),
    upi: z.string().optional(),
    iso: z.string().optional(),
    additionalInfo: z.string().optional(),
  });

  const emptyForm = {
    ifscCode: "",
    bankName: "",
    branchName: "",
    state: "",
    district: "",
    center: "",
    cityName: "",
    micrCode: "",
    swiftCode: "",
    iso: "",
    branchContact: "",
    branchAddress: "",
    AdditionalInfo: "",
    imps: "",
    neft: "",
    rtgs: "",
    upi: "",
  };

  const indianStates = [
    "ANDHRA PRADESH",
    "ARUNACHAL PRADESH",
    "ASSAM",
    "BIHAR",
    "CHHATTISGARH",
    "GOA",
    "GUJARAT",
    "HARYANA",
    "HIMACHAL PRADESH",
    "JHARKHAND",
    "KARNATAKA",
    "KERALA",
    "MADHYA PRADESH",
    "MAHARASHTRA",
    "MANIPUR",
    "MEGHALAYA",
    "MIZORAM",
    "NAGALAND",
    "ODISHA",
    "PUNJAB",
    "RAJASTHAN",
    "SIKKIM",
    "TAMIL NADU",
    "TELANGANA",
    "TRIPURA",
    "UTTAR PRADESH",
    "UTTARAKHAND",
    "WEST BENGAL",
  ];

  const inputDetails = [
    {
      name: "ifscCode",
      label: "IFSC Code",
      placeholder: "SBIN0006162",
      maxLength: 11,
      uppercase: true,
      required: true,
      type: "text",
      grid: "col-span-1",
    },
    {
      name: "bankName",
      label: "Bank Name",
      placeholder: "State Bank of India",
      required: true,
      type: "text",
      grid: "col-span-1",
    },
    {
      name: "branchName",
      label: "Branch Name",
      placeholder: "Varanasi Main Branch",
      required: true,
      type: "text",
      grid: "col-span-1",
    },
    {
      name: "state",
      label: "State",
      placeholder: "Uttar Pradesh",
      required: true,
      type: "select",
      grid: "col-span-1",
      options: indianStates,
    },
    {
      name: "district",
      label: "District",
      placeholder: "Varanasi",
      required: true,
      type: "text",
      grid: "col-span-1",
    },
    {
      name: "center",
      label: "Center",
      placeholder: "Varanasi",
      required: false,
      type: "text",
      grid: "col-span-1",
    },

    {
      name: "cityName",
      label: "City Name",
      placeholder: "Varanasi",
      required: false,
      type: "text",
      grid: "col-span-1",
    },
    {
      name: "micrCode",
      label: "MICR Code",
      placeholder: "221002002",
      required: false,
      maxLength: 9,

      type: "text",
      grid: "col-span-1",
    },
    {
      name: "swiftCode",
      label: "SWIFT Code",
      placeholder: "SBININBB123",
      required: false,
      type: "text",
      grid: "col-span-1",
    },
    {
      name: "iso",
      label: "ISO Code",
      placeholder: "IN",
      required: false,
      type: "text",
      grid: "col-span-1",
    },
    {
      name: "branchContact",
      label: "Branch Contact",
      placeholder: "0542-1234567",
      required: false,
      type: "text",
      grid: "col-span-1",
    },
    {
      name: "branchAddress",
      label: "Branch Address",
      placeholder: "Bhelupur, Varanasi",
      required: false,
      type: "textarea",
      grid: "md:col-span-3 lg:col-span-2  sm:col-span-2 col-span-1",
    },

    {
      name: "AdditionalInfo",
      label: "Additional Info",
      placeholder: "24/7 ATM available",
      required: false,
      type: "textarea",
      grid: "md:col-span-3 lg:col-span-2 sm:col-span-2 col-span-1",
    },
    {
      name: "imps",
      label: "IMPS",
      required: false,
      type: "select",
      grid: "col-span-1",
      options: [true, false],
    },
    {
      name: "neft",
      label: "NEFT",
      required: false,
      type: "select",
      grid: "col-span-1",
      options: [true, false],
    },
    {
      name: "rtgs",
      label: "RTGS",
      required: false,
      type: "select",
      grid: "col-span-1",
      options: [true, false],
    },
    {
      name: "upi",
      label: "UPI",
      required: false,
      type: "select",
      grid: "col-span-1",
      options: [true, false],
    },
  ];

  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(schema),
    mode: "onBlur",
  });

  const onSubmit = async (data) => {
    console.log("Form data:", data);

    data.imps = data.imps === "true";
    data.neft = data.neft === "true";
    data.rtgs = data.rtgs === "true";
    data.upi = data.upi === "true";

    const payload = {};

    if (data?.bankName) payload.bank = data.bankName;
    if (data?.ifscCode) payload.ifsc = data.ifscCode;
    if (data?.branchName) payload.branch = data.branchName;
    if (data?.district) payload.district = data.district;
    if (data?.state) payload.state = data.state;
    if (data?.cityName) payload.city = data.cityName;
    if (data?.branchAddress) payload.address = data.branchAddress;
    if (data?.branchContact) payload.contact = data.branchContact;
    if (data?.micrCode) payload.micr = data.micrCode;
    if (data?.upi !== undefined) payload.upi = data.upi;
    if (data?.imps !== undefined) payload.imps = data.imps;
    if (data?.rtgs !== undefined) payload.rtgs = data.rtgs;
    if (data?.neft !== undefined) payload.neft = data.neft;
    if (data?.swiftCode) payload.swift = data.swiftCode;
    if (data?.center) payload.centre = data.center;
    if (data?.iso) payload.iso3166 = data.iso;
    if (data?.AdditionalInfo) payload.additional_info = data.AdditionalInfo;

    setLoading(true);
    try {
      const res = await createIfsc(payload);

      if (
        res?.status?.code === 201 ||
        res?.status?.code === 201 ||
        res?.status?.code === 200 ||
        res?.status?.code === 200
      ) {
        showSuccessToast(
          res?.message || res?.data?.message || res?.data?.status?.message,
        );
        reset(emptyForm);
      } else {
        const errorMessage =
          res?.data?.status?.message?.ifsc?.[0] ||
          res?.data?.errors ||
          res?.data?.status?.message ||
          "Failed";

        showErrorToast(errorMessage);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const onUpdate = async (data) => {
    console.log("Form data:", data);

    data.imps = data.imps === "true";
    data.neft = data.neft === "true";
    data.rtgs = data.rtgs === "true";
    data.upi = data.upi === "true";

    const payload = {};

    if (data?.bankName) payload.bank = data.bankName;
    if (data?.ifscCode) payload.ifsc = data.ifscCode;
    if (data?.branchName) payload.branch = data.branchName;
    if (data?.district) payload.district = data.district;
    if (data?.state) payload.state = data.state;
    if (data?.cityName) payload.city = data.cityName;
    if (data?.branchAddress) payload.address = data.branchAddress;
    if (data?.branchContact) payload.contact = data.branchContact;
    if (data?.micrCode) payload.micr = data.micrCode;
    if (data?.upi !== undefined) payload.upi = data.upi;
    if (data?.imps !== undefined) payload.imps = data.imps;
    if (data?.rtgs !== undefined) payload.rtgs = data.rtgs;
    if (data?.neft !== undefined) payload.neft = data.neft;
    if (data?.swiftCode) payload.swift = data.swiftCode;
    if (data?.center) payload.centre = data.center;
    if (data?.iso) payload.iso3166 = data.iso;
    if (data?.AdditionalInfo) payload.additional_info = data.AdditionalInfo;
    payload.id = dataToUpdate.id;

    setLoading(true);
    try {
      const res = await updateIfsc(payload);

      if (res?.status?.code === 200 || res?.data?.status?.code === 200) {
        showSuccessToast(
          res?.message || res?.data?.message || res?.data?.status?.message,
        );
        setFormType("create");
        reset(emptyForm);
      } else {
        const errorMessage =
          res?.data?.status?.message?.ifsc?.[0] ||
          res?.data?.errors ||
          res?.data?.status?.message ||
          "Failed";

        showErrorToast(errorMessage);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    reset({
      ifscCode: dataToUpdate.ifsc,
      bankName: dataToUpdate.bank || "",
      branchName: dataToUpdate.branch || "",
      state: dataToUpdate.state || "",
      district: dataToUpdate.district || "",
      center: dataToUpdate.centre || "",
      cityName: dataToUpdate.city || "",
      micrCode: dataToUpdate.micr || "",
      swiftCode: dataToUpdate.swift || "",
      iso: dataToUpdate.iso3166 || "",
      branchContact: dataToUpdate.contact || "",
      branchAddress: dataToUpdate.address || "",
      AdditionalInfo: dataToUpdate.additional_info || "",
      imps: Boolean(dataToUpdate.imps) || "",
      neft: Boolean(dataToUpdate.neft) || "",
      rtgs: Boolean(dataToUpdate.rtgs) || "",
      upi: Boolean(dataToUpdate.upi) || "",
    });
  };

  useEffect(() => {
    if (dataToUpdate?.id) {
      handleEdit();
    }
  }, [dataToUpdate]);

  return (
    <form
      onSubmit={handleSubmit(formType === "create" ? onSubmit : onUpdate)}
      className="pt-4"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {inputDetails.map((field) => {
          if (field.type === "textarea") {
            return (
              <div key={field.name} className={field.grid}>
                <label className="font-semibold">
                  {field.label}

                  {field?.required && (
                    <span className="text-red-500 text-sm"> *</span>
                  )}
                </label>

                <textarea
                  {...register(field.name)}
                  placeholder={field.placeholder}
                  className={`w-full border rounded-lg px-3 py-2 mt-1 focus:ring-2 focus:ring-indigo-400 ${field.grid}`}
                />

                {errors[field.name] && (
                  <p className="text-red-500 text-sm">
                    {errors[field.name]?.message}
                  </p>
                )}
              </div>
            );
          }
          if (field.type === "select") {
            return (
              <div key={field.name} className={field.grid}>
                <label className="font-semibold">
                  {field.label}

                  {field?.required && (
                    <span className="text-red-500 text-sm"> *</span>
                  )}
                </label>

                <select
                  {...register(field.name)}
                  className="w-full border rounded-lg px-3 py-2 mt-1 focus:ring-2 focus:ring-indigo-400"
                >
                  <option value="">Select {field.label}</option>

                  {field.options?.map((option, i) => (
                    <option key={i} value={option.toString()}>
                      {option.toString()}
                    </option>
                  ))}
                </select>

                {errors[field.name] && (
                  <p className="text-red-500 text-sm">
                    {errors[field.name]?.message}
                  </p>
                )}
              </div>
            );
          }

          return (
            <div key={field.name} className={field.grid}>
              <label className="font-semibold">
                {field.label}

                {field?.required && (
                  <span className="text-red-500 text-sm"> *</span>
                )}
              </label>

              <input
                type={field.type}
                {...register(field.name)}
                placeholder={field.placeholder}
                maxLength={field?.maxLength}
                className={`w-full border rounded-lg px-3 py-2 mt-1 focus:ring-2 focus:ring-indigo-400 ${field?.uppercase && "uppercase"}`}
              />

              {errors[field.name] && (
                <p className="text-red-500 text-sm">
                  {errors[field.name]?.message}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Buttons */}
      <div className="mt-8 flex justify-end gap-4">
        <button
          onClick={() => reset(emptyForm)}
          className="px-5 py-2 border rounded-lg"
        >
          Reset
        </button>
        <button
          type="submit"
          className={`px-6 py-2   ${formType === "create" ? "bg-indigo-600 text-white rounded-lg hover:bg-indigo-700" : "bg-amber-500 text-black rounded-lg hover:bg-amber-700"}`}
        >
          {loading ? (
            <Loader2 className="animate-spin" />
          ) : formType === "create" ? (
            "create Branch"
          ) : (
            "Update Branch"
          )}
        </button>
      </div>
    </form>
  );
}
